import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, FlatList, StyleSheet, Platform, TouchableOpacity, Dimensions} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Snackbar,
  Menu,
  Chip,
  ActivityIndicator,
  Portal,
  Modal,
  RadioButton,
  Divider,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation, useRoute} from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useExpenseStore} from '../../store/useExpenseStore';
import {useExpenseCategoryStore} from '../../store/useExpenseCategoryStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE'];

const ExpenseFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const existingExpense = route.params?.expense;
  const isEdit = !!existingExpense;

  const {createExpense, updateExpense} = useExpenseStore();
  const {expenseCategories, fetchExpenseCategories} = useExpenseCategoryStore();
  const {orgUnits, fetchOrgUnits} = useOrgUnitStore();

  const [amount, setAmount] = useState(
    existingExpense ? String(existingExpense.amount ?? existingExpense.totalAmount ?? '') : '',
  );
  const [purpose, setPurpose] = useState(
    existingExpense?.purpose ?? existingExpense?.description ?? '',
  );
  const [merchant, setMerchant] = useState(
    existingExpense?.merchant ?? existingExpense?.vendorName ?? '',
  );
  const [expenseCategoryId, setExpenseCategoryId] = useState(
    existingExpense?.expenseCategoryId || null,
  );
  const [paymentMode, setPaymentMode] = useState(
    existingExpense?.paymentMode || 'CASH',
  );
  const [expenseDate, setExpenseDate] = useState(
    existingExpense?.expenseDate
      ? new Date(existingExpense.expenseDate)
      : new Date(),
  );
  const [staffId, setStaffId] = useState(existingExpense?.staffId || null);
  const [orgUnitId, setOrgUnitId] = useState(existingExpense?.orgUnitId || null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [modeMenuVisible, setModeMenuVisible] = useState(false);
  const [staffMenuVisible, setStaffMenuVisible] = useState(false);
  const [storeModalVisible, setStoreModalVisible] = useState(false);

  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);

  useEffect(() => {
    fetchExpenseCategories();
    loadWorkers();
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [fetchExpenseCategories, fetchOrgUnits, orgUnits.length]);

  // Auto-select if only one store exists
  useEffect(() => {
    if (orgUnits.length === 1 && !orgUnitId) {
      setOrgUnitId(orgUnits[0].id);
    }
  }, [orgUnits, orgUnitId]);

  const loadWorkers = async () => {
    setLoadingWorkers(true);
    try {
      const response = await workerApi.list();
      const wd = response.data.data;
      setWorkers(wd?.content || wd || []);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setLoadingWorkers(false);
    }
  };

  const selectedCategoryName =
    expenseCategories.find(c => c.id === expenseCategoryId)?.name ||
    t('expense.category');

  const selectedStaffName =
    workers.find(w => w.id === staffId)?.fullName || t('expense.staff');

  const formatDate = d =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const handleSave = useCallback(async () => {
    const parsedAmount = parseFloat(amount);
    if (!purpose.trim() || !expenseCategoryId || !amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      setSnackbar(t('common.required'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        purpose: purpose.trim(),
        merchant: merchant.trim() || null,
        expenseCategoryId,
        expenseDate: formatDate(expenseDate),
        amount: parsedAmount,
        staffId: staffId || null,
        paymentMode,
        orgUnitId: orgUnitId || null,
      };

      if (isEdit) {
        await updateExpense(existingExpense.id, payload);
      } else {
        await createExpense(payload);
      }
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [
    purpose,
    merchant,
    expenseCategoryId,
    expenseDate,
    amount,
    staffId,
    paymentMode,
    orgUnitId,
    isEdit,
    existingExpense,
    createExpense,
    updateExpense,
    navigation,
    t,
  ]);

  return (
    <View style={styles.container}>
      <GradientHeader
        title={isEdit ? t('expense.editExpense') : t('expense.captureExpense')}
        onBack={() => navigation.goBack()}
      />

      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Store selection - quick boxes */}
        {orgUnits.length > 1 && (
          <>
            <Text variant="titleMedium" style={styles.stepTitle}>{t('expense.store')}</Text>
            {orgUnits.length <= 4 ? (
              <View style={styles.storeRow}>
                {orgUnits.map(store => (
                  <Chip
                    key={store.id}
                    selected={orgUnitId === store.id}
                    showSelectedCheck={orgUnitId === store.id}
                    selectedColor={orgUnitId === store.id ? colors.white : undefined}
                    onPress={() => setOrgUnitId(store.id)}
                    style={[styles.storeChip, orgUnitId === store.id && styles.storeChipActive]}
                    textStyle={orgUnitId === store.id ? styles.storeChipTextActive : styles.storeChipText}>
                    <Text style={{fontSize: 13, color: orgUnitId === store.id ? '#fff' : '#000', marginTop: 0}}>{store.name}</Text>
                  </Chip>
                ))}
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.storeDropdown}
                  onPress={() => setStoreModalVisible(true)}
                  activeOpacity={0.7}>
                  <MaterialCommunityIcons name="store-outline" size={20} color={orgUnitId ? colors.primary : colors.textSecondary} />
                  <Text style={[styles.storeDropdownText, orgUnitId && styles.storeDropdownTextSelected]} numberOfLines={1}>
                    {orgUnitId ? orgUnits.find(s => s.id === orgUnitId)?.name : t('expense.store')}
                  </Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
                <Portal>
                  <Modal
                    visible={storeModalVisible}
                    onDismiss={() => setStoreModalVisible(false)}
                    contentContainerStyle={styles.storeModal}>
                    <Text style={styles.storeModalTitle}>{t('expense.store')}</Text>
                    <Divider />
                    <FlatList
                      data={orgUnits}
                      keyExtractor={item => String(item.id)}
                      renderItem={({item}) => (
                        <TouchableOpacity
                          style={styles.storeOption}
                          onPress={() => { setOrgUnitId(item.id); setStoreModalVisible(false); }}>
                          <RadioButton.Android
                            value={String(item.id)}
                            status={orgUnitId === item.id ? 'checked' : 'unchecked'}
                            onPress={() => { setOrgUnitId(item.id); setStoreModalVisible(false); }}
                            color={colors.primary}
                          />
                          <Text style={styles.storeOptionText}>{item.name}</Text>
                        </TouchableOpacity>
                      )}
                    />
                  </Modal>
                </Portal>
              </>
            )}
          </>
        )}

        {/* Amount */}
        <TextInput
          label={t('expense.amount')}
          value={amount}
          onChangeText={setAmount}
          mode="outlined"
          keyboardType="numeric"
          style={styles.amountInput}
          left={<TextInput.Affix text={'\u20B9'} />}
        />

        {/* Purpose */}
        <TextInput
          label={t('expense.purpose')}
          value={purpose}
          onChangeText={setPurpose}
          mode="outlined"
          style={styles.input}
        />

        {/* Merchant */}
        <TextInput
          label={t('expense.merchant')}
          value={merchant}
          onChangeText={setMerchant}
          mode="outlined"
          style={styles.input}
        />

        {/* Category dropdown */}
        <Menu
          visible={categoryMenuVisible}
          onDismiss={() => setCategoryMenuVisible(false)}
          anchor={
            <TouchableOpacity activeOpacity={0.7} onPress={() => setCategoryMenuVisible(true)}>
              <View pointerEvents="none">
                <TextInput
                  label={t('expense.category')}
                  value={selectedCategoryName}
                  mode="outlined"
                  style={styles.input}
                  editable={false}
                  right={<TextInput.Icon icon="chevron-down" />}
                />
              </View>
            </TouchableOpacity>
          }>
          {expenseCategories.map(cat => (
            <Menu.Item
              key={cat.id}
              title={cat.name}
              onPress={() => {
                setExpenseCategoryId(cat.id);
                setCategoryMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        {/* Payment Mode dropdown */}
        <Menu
          visible={modeMenuVisible}
          onDismiss={() => setModeMenuVisible(false)}
          anchor={
            <TouchableOpacity activeOpacity={0.7} onPress={() => setModeMenuVisible(true)}>
              <View pointerEvents="none">
                <TextInput
                  label={t('expense.paymentMode')}
                  value={paymentMode.replace(/_/g, ' ')}
                  mode="outlined"
                  style={styles.input}
                  editable={false}
                  right={<TextInput.Icon icon="chevron-down" />}
                />
              </View>
            </TouchableOpacity>
          }>
          {PAYMENT_MODES.map(mode => (
            <Menu.Item
              key={mode}
              title={mode.replace(/_/g, ' ')}
              onPress={() => {
                setPaymentMode(mode);
                setModeMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        {/* Date picker */}
        <TouchableOpacity activeOpacity={0.7} onPress={() => setShowDatePicker(true)}>
          <View pointerEvents="none">
            <TextInput
              label={t('expense.expenseDate')}
              value={formatDate(expenseDate)}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="calendar" />}
            />
          </View>
        </TouchableOpacity>
        {showDatePicker && (
          <DateTimePicker
            value={expenseDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={(event, selectedDate) => {
              setShowDatePicker(false);
              if (selectedDate) {
                setExpenseDate(selectedDate);
              }
            }}
          />
        )}

        {/* Staff dropdown */}
        <Menu
          visible={staffMenuVisible}
          onDismiss={() => setStaffMenuVisible(false)}
          anchor={
            <TouchableOpacity activeOpacity={0.7} onPress={() => setStaffMenuVisible(true)}>
              <View pointerEvents="none">
                <TextInput
                  label={t('expense.staff')}
                  value={selectedStaffName}
                  mode="outlined"
                  style={styles.input}
                  editable={false}
                  right={
                    loadingWorkers ? (
                      <TextInput.Icon icon={() => <ActivityIndicator size={16} />} />
                    ) : (
                      <TextInput.Icon icon="chevron-down" />
                    )
                  }
                />
              </View>
            </TouchableOpacity>
          }>
          {workers.map(w => (
            <Menu.Item
              key={w.id}
              title={w.fullName}
              onPress={() => {
                setStaffId(w.id);
                setStaffMenuVisible(false);
              }}
            />
          ))}
        </Menu>

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          contentStyle={styles.saveButtonContent}>
          {isEdit ? t('expense.update') : t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {flex: 1, padding: spacing.md},
  stepTitle: {fontWeight: 'bold', color: colors.text, marginBottom: spacing.md},
  storeRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.md,
  },
  storeChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  storeChipActive: {backgroundColor: colors.primary, borderColor: colors.primary},
  storeChipText: {color: colors.text, fontSize: 13},
  storeChipTextActive: {color: colors.white, fontSize: 13},
  storeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    marginBottom: spacing.md,
  },
  storeDropdownText: {flex: 1, marginLeft: spacing.sm, fontSize: 15, color: colors.textSecondary},
  storeDropdownTextSelected: {color: colors.text},
  storeModal: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    borderRadius: 12,
    height: Dimensions.get('window').height * 0.5,
    overflow: 'hidden',
  },
  storeModalTitle: {fontSize: 16, fontWeight: '600', color: colors.text, padding: spacing.md},
  storeOption: {flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, paddingRight: spacing.md},
  storeOptionText: {fontSize: 15, color: colors.text},
  amountInput: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    fontSize: 24,
  },
  input: {
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
  },
  saveButton: {
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
    backgroundColor: colors.primary,
  },
  saveButtonContent: {
    paddingVertical: spacing.xs,
  },
});

export default ExpenseFormScreen;
